import { useMemo, useState } from 'react';
import moment from 'moment';
import { getAlertsSummaryData } from '@src/api/aiAlerts';
import { useQuery } from 'react-query';
import { formatSummaryData, sortColumn } from '@store/aiAlerts/summary/helper';
import { useSelector } from 'react-redux';
import {
  aiAlertsDimensionMappingSelector,
  aiAlertsMetricsMetaSelector,
} from '@store/common/selectors';
import { dimensionsListSelectorKeyValue } from '@store/common/dimensionList/selectors';
import { get, isEmpty } from 'lodash';
import { filterAlerts } from './utils';
import { trackQueryError } from '@utils/sensor/apptracker';

export function useFetchCauseAlerts({ interval, enabled = false, selectedAlert, sortBy }) {
  const dimensionListObject = useSelector(dimensionsListSelectorKeyValue);
  const dimensionMapping = useSelector(aiAlertsDimensionMappingSelector);
  const aiAlertsMetricMeta = useSelector(aiAlertsMetricsMetaSelector);
  const defaultValue = [];

  const { alertId = null } = selectedAlert;

  // stale time prevents redundant calls from multiple subscribers
  const { data: response, isFetching } = useQuery(
    ['ai-alert-summary-widgets', alertId, interval],
    () => {
      return getAlertsSummaryData({ params: { ...interval, cache: true } }).then((response) => {
        const data = get(response, ['data'], {});
        if (!isEmpty(data)) {
          const filteredAlertData = filterAlerts(selectedAlert, data);
          const { alerts } = formatSummaryData({
            response: filteredAlertData,
            customerNames: {},
            dimensionListObject,
            dimensionMapping,
            aiAlertsMetricMeta,
          });
          return alerts;
        }
        return [];
      });
    },
    {
      retry: 3,
      refetchOnWindowFocus: false,
      enabled: enabled && !!interval && !!alertId,
      staleTime: 5 * 60 * 1000, // 5m
      placeholderData: defaultValue,
      onError: (err) => {
        trackQueryError({ type: 'ai-alert-summary', err });
      },
    },
  );

  const data = useMemo(() => {
    if (!enabled || isEmpty(response)) {
      return defaultValue;
    }
    return response;
  }, [enabled, response]);

  const alerts = data;

  // sort data
  if (sortBy) {
    sortColumn(sortBy, alerts);
  }

  return { alerts, isFetching };
}

function useCauseAlerts({ alert, enabled = true }) {
  const [sortBy, setSortBy] = useState({
    key: 'eventTime',
    order: 'desc',
  });
  const onColumnSort = ({ key, order }: any) => {
    setSortBy({ key, order });
  };

  const interval = useMemo(() => {
    const to = moment().startOf('hour');
    const from = moment(to).subtract(30, 'days').startOf('hour');
    return { from: from.format(), to: to.format() };
  }, []);

  const selectedAlert = useMemo(() => {
    return get(alert, ['diagnosticsData', 'summary'], null);
  }, [alert]);

  const { alerts, isFetching } = useFetchCauseAlerts({ interval, enabled, selectedAlert, sortBy });

  return { alerts, isFetching, interval, sortBy, onColumnSort };
}

export default useCauseAlerts;
